import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FiFolder, FiGithub, FiExternalLink, FiX, FiChevronLeft, FiChevronRight, FiPlus } from 'react-icons/fi';

const Section = styled.section`
  padding: 8rem 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 1.25rem;
  }
`;

const SectionHeader = styled(motion.div)`
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    &::before {
      content: '03.';
      font-family: 'JetBrains Mono', monospace;
      font-size: 1.25rem;
      color: ${props => props.theme.colors.primary};
    }
    
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${props => props.theme.colors.border};
      margin-left: 2rem;
    }
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
`;

const ProjectCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    padding: 1px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 
      0 20px 40px -15px rgba(0, 0, 0, 0.6),
      0 0 30px rgba(56, 189, 248, 0.1);
    
    &::before {
      opacity: 1;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  .folder {
    color: ${props => props.theme.colors.primary};
    font-size: 2.5rem;
    transition: transform 0.3s;
  }
  
  ${ProjectCard}:hover & .folder {
    transform: scale(1.1) rotate(-5deg);
  }
  
  .links {
    display: flex;
    gap: 1rem;
    
    a {
      color: ${props => props.theme.colors.textDim};
      font-size: 1.25rem;
      transition: all 0.3s;
      padding: 0.5rem;
      border-radius: 6px;
      
      &:hover {
        color: ${props => props.theme.colors.primary};
        background: rgba(56, 189, 248, 0.1);
      }
    }
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  font-weight: 700;
  transition: color 0.3s;
  
  ${ProjectCard}:hover & {
    color: ${props => props.theme.colors.primary};
  }
`;

const ProjectDescription = styled.div`
  color: ${props => props.theme.colors.textDim};
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  flex: 1;
`;

const TechStack = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  list-style: none;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  
  li {
    color: ${props => props.theme.colors.primary};
    background: rgba(56, 189, 248, 0.1);
    padding: 0.35rem 0.75rem;
    border-radius: 4px;
    border: 1px solid rgba(56, 189, 248, 0.2);
    transition: all 0.3s;
    
    &:hover {
      background: rgba(56, 189, 248, 0.2);
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const ViewButton = styled.button`
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.2);
  color: ${props => props.theme.colors.primary};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-left: 0.5rem;
  font-size: 1.2rem;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: #000;
    transform: rotate(90deg);
    box-shadow: 0 0 15px rgba(56, 189, 248, 0.4);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  width: 100%;
  max-width: 900px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16/9;
  box-shadow: 0 0 50px rgba(56, 189, 248, 0.1);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    aspect-ratio: 4/3;
  }
`;

const ImageSlider = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const SlideImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -3rem;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  z-index: 1001;
  transition: color 0.3s;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    top: -2.5rem;
    right: 0.5rem;
    font-size: 1.5rem;
  }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: #000;
  }
  
  &.prev { left: 1rem; }
  &.next { right: 1rem; }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
    
    &.prev { left: 0.5rem; }
    &.next { right: 0.5rem; }
  }
`;

const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const projects = [
    {
      title: "Operations Web System (KR/JP)",
      description: "한국과 일본의 상담 운영 효율 개선을 위해 데이터 배분부터 성과 모니터링이 통합된 웹 시스템입니다. 수동 처리하던 데이터 배분과 타입 구분 과정을 자동화 파이프라인으로 대체하여 처리량을 70% 이상 향상시켰습니다.",
      tech: ["Python", "JavaScript", "HTML5/CSS3", "React", "FastAPI", "GitHub Actions", "MySQL", "Hive"],
      images: [
        "/screenshots/1.png",
        "/screenshots/2.png",
        "/screenshots/3.png",
        "/screenshots/7.png"
      ],
      github: null,
      link: null
    },
    {
      title: "Assign Automation Bot (KR/JP)",
      description: "지연 주문을 식별하는 쿼리문을 만들어 ClickHouse에서 데이터를 추출 Logistic Regression 모델로 처리 우선순위를 구분 합니다.Selenium을 사용한 배정 봇으로 구축해 결과적으로 연간 4~5억 원의 비용을 절감하였습니다. ",
      tech: ["Python", "Fast API", "GitHub Actions", "My sql", "Hive", "Selenium", "Machine Learning"],
      images: [
        "/screenshots/4.png",
        "/screenshots/6.png"

      ],
      github: null,
      link: null
    },
    {
      title: "AI Data Agent",
      description: "LLM을 사용하기 위해 메타 데이터 및 프롬프트 엔지니어링 하여 Text-to-SQL 이 가능한 쳇봇 형태입니다. 비개발자도 원하는 데이터를 조회할 수있게 되어 프로덕트에 요청 하던 리드타임을 80% 이상 단축, 실무자는 문제 해결에만 집중할 수 있는 업무 환경으로 개선 했습니다.",
      tech: ["Python", "Fast API", "Hive","Clickhouse","Mimir", "Prompt Engineering"],
      images: [
        "/screenshots/8.png"
      ],
      github: null,
      link: null
    },
    {
      title: "Analyzer Agent",
      description: "Playwright로 타겟 데이터를 수집하는 자동화 파이프라인을 만들고, LLM에 프롬프트 엔지니어으로 감정 분류를 키워드별로 감정 분석(긍정/부정/중립)을 자동화, 핵심 키워드만 정렬하고 요약을 제공하여 정보 수집에 소요되는 리소스 및 데이터 의사결정 속도를 개선 했습니다.",
      tech: ["Python", "Fast API", "React", "Playwright", "Prompt Engineering"],
      images: [
        "/screenshots/12.png"
      ],
      github: null,
      link: null
    },
    {
      title: "Real-time Action Detection System",
      description: "실시간 이상행동 모델 개발입니다. 딥러닝 모델(LSTM)의 정확도 개선을 위해 절대 좌표를 벡터 데이터로 변환하는 전처리 로직을 설계하였으며, WebSocket을 활용해 감지된 위험 상황을 사용자에게 즉각 알림으로 전달하고 대시보드를 서비스를 구축 하였습니다.",
      tech: ["Python", "Fast API", "Tensorflow", "OpenVINO", "WebSocket", "AWS"],
      images: [
        "/screenshots/13.jpg",
        "/screenshots/14.png"
      ],
      github: null,
      link: null
    },
    {
      title: "Stray Animal Adoption Prediction & Analysis",
      description: "10만 건 이상의 유기동물 데이터를 수집하였으며, 히스토그램 그래디언트 부스팅 모델을 통해 입양률을 예측을 통한 관리 컨설팅을 하는 서비스 입니다. CNN 모델로 유기동물의 사진만으로 품종과 나이를 예측하는 하는 모델을 개발하여 유기동물의 주인을 찾고자 하였습니다.",
      tech: ["Python", "BeautifulSoup", "Scikit-learn", "Tensorflow(CNN)", "Pandas", "Folium"],
      images: [
        "/screenshots/15.jpg"
      ],
      github: null,
      link: null
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  const handleProjectClick = (project) => {
    console.log("Project clicked:", project.title);
    if (project.images) {
      console.log("Opening gallery for:", project.title);
      setSelectedProject(project);
      setCurrentImageIndex(0);
    }
  };

  return (
    <Section id="projects">
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>Featured Projects</h2>
        </SectionHeader>
        
        <ProjectsGrid
          ref={ref}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              variants={cardVariants}
              onClick={() => handleProjectClick(project)}
              style={{ cursor: project.images ? 'pointer' : 'default' }}
            >
              <CardHeader>
                <div className="folder">
                  <FiFolder />
                </div>
                <div className="links">
                  {project.github && (
                    <a href={project.github} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                      <FiGithub />
                    </a>
                  )}
                  {project.link && (
                    <a href={project.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                      <FiExternalLink />
                    </a>
                  )}
                  {project.images && (
                    <ViewButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectClick(project);
                      }}
                      title="View Gallery"
                    >
                      <FiPlus />
                    </ViewButton>
                  )}
                </div>
              </CardHeader>
              
              <ProjectTitle>{project.title}</ProjectTitle>
              
              <ProjectDescription>
                {project.description}
              </ProjectDescription>
              
              <TechStack>
                {project.tech.map((tech, i) => (
                  <li key={i}>{tech}</li>
                ))}
              </TechStack>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>

      <AnimatePresence>
        {selectedProject && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <div style={{ position: 'relative', width: '100%', maxWidth: '900px' }}>
              <CloseButton onClick={() => setSelectedProject(null)}>
                <FiX />
              </CloseButton>
              <ModalContent
                layoutId={`project-${selectedProject.title}`}
                onClick={(e) => e.stopPropagation()}
              >
                <ImageSlider>
                  <AnimatePresence mode="wait">
                    {selectedProject.images && (
                      <SlideImage
                        key={currentImageIndex}
                        src={selectedProject.images[currentImageIndex]}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </AnimatePresence>
                  
                  {selectedProject.images && selectedProject.images.length > 1 && (
                    <>
                      <NavButton className="prev" onClick={prevImage}>
                        <FiChevronLeft />
                      </NavButton>
                      <NavButton className="next" onClick={nextImage}>
                        <FiChevronRight />
                      </NavButton>
                    </>
                  )}
                </ImageSlider>
              </ModalContent>
            </div>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Section>
  );
};

export default Projects;
